/**
 * The characters' extra items (docs/refactor/47): hats, what is held in the hunting pose, RPG and sport outfits, and a
 * companion floating beside them. In the office's own style — 2D cartoon, dark outlines — and drawn in the figure's
 * coordinates (Figures.tsx): the head centred at (0, -100), radius about 23; the torso from y -75.5 to -36; the held
 * item in the racket's frame (its hand at the handle's end, y 12 to 28, the item rising towards negative y).
 * Which of them are Pro is the server's list (DeveloperAvatar.Premium), mirrored in wardrobe.ts.
 */

import type { ReactNode } from 'react'
import type { CompanionKind, ExtraKind, HatKind, HeldKind } from './wardrobe'

const INK = '#1a1f2c'
const GOLD = '#F5C542'
const STEEL = '#C9D2DC'
const STEEL_DARK = '#8E9AAB'
const WOOD = '#8B5A2B'

/** A hat the person chose: drawn over hair and headphones, like the season's hats it takes the place of. */
export function ChosenHat({ kind }: { kind: HatKind }) {
  switch (kind) {
    case 'cap':
      return (
        <g>
          <path d="M -22.5 -108 Q -23 -129 0 -129 Q 23 -129 22.5 -108 Z" fill="#D7263D" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M 8 -110 Q 34 -113 39 -106 Q 27 -102 9 -105 Z" fill="#B51F33" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M 0 -129 L 0 -110" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" />
          <circle cx="0" cy="-129" r="2.2" fill="#B51F33" stroke={INK} strokeWidth="1.1" />
        </g>
      )
    case 'propeller':
      // The kids' propeller cap (docs/refactor/48): four colours and a little propeller that spins.
      return (
        <g>
          <path d="M -22.5 -108 Q -23 -129 0 -129 Q 23 -129 22.5 -108 Z" fill="#3E9BE6" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M -22.5 -108 Q -16 -126 0 -129 L 0 -108 Z" fill="#F5C542" />
          <path d="M 0 -129 Q 14 -127 22.5 -108 L 0 -108 Z" fill="#D7263D" />
          <path d="M -22.5 -108 Q -23 -129 0 -129 Q 23 -129 22.5 -108 Z" fill="none" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M 8 -110 Q 30 -112 34 -106 Q 24 -103 9 -105 Z" fill="#2F9E68" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M 0 -129 L 0 -135" stroke={INK} strokeWidth="1.6" />
          <g className="bh-propeller">
            <ellipse cx="-7" cy="-136" rx="7" ry="2.2" fill="#F5C542" stroke={INK} strokeWidth="1.1" />
            <ellipse cx="7" cy="-136" rx="7" ry="2.2" fill="#D7263D" stroke={INK} strokeWidth="1.1" />
          </g>
          <circle cx="0" cy="-136" r="1.8" fill={INK} />
        </g>
      )
    case 'wizard':
      return (
        <g>
          <ellipse cx="0" cy="-119" rx="33" ry="6.5" fill="#3B3F9E" stroke={INK} strokeWidth="1.5" />
          <path d="M -17 -121 Q -9 -142 6 -163 Q 13 -170 19 -164 Q 12 -160 11 -152 Q 14 -137 18 -121 Z" fill="#4A4FC4" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M -16.5 -127 L 17.7 -127 L 18 -121.5 L -17 -121.5 Z" fill={GOLD} stroke={INK} strokeWidth="0.9" />
          <Star x={-4} y={-140} r={3.4} />
          <Star x={7} y={-150} r={2.4} />
          <circle cx="2" cy="-133" r="1.1" fill="#FFF4C2" />
          <circle cx="19" cy="-164" r="2.4" fill={GOLD} stroke={INK} strokeWidth="1" />
        </g>
      )
    case 'helmet':
      return (
        <g>
          <path d="M 0 -128 Q 5 -146 19 -146 Q 11 -140 9 -128 Z" fill="#D7263D" stroke={INK} strokeWidth="1.3" strokeLinejoin="round" />
          <path d="M -24.5 -103 Q -25 -129 0 -129.5 Q 25 -129 24.5 -103 Z" fill={STEEL} stroke={INK} strokeWidth="1.7" strokeLinejoin="round" />
          <path d="M -24.5 -108 L 24.5 -108 L 24.6 -103 L -24.6 -103 Z" fill={STEEL_DARK} stroke={INK} strokeWidth="1.2" />
          <path d="M 0 -129 L 0 -108" stroke={STEEL_DARK} strokeWidth="2.2" />
          <rect x="-2.2" y="-108" width="4.4" height="13" rx="1.5" fill={STEEL} stroke={INK} strokeWidth="1.2" />
          {[-18, -9, 9, 18].map((x) => <circle key={x} cx={x} cy="-105.5" r="0.9" fill={INK} />)}
          <path d="M -15 -124 Q -10 -127 -5 -126" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.6" strokeLinecap="round" />
        </g>
      )
    case 'crown':
      return (
        <g>
          <path d="M -17 -117 L -18 -134 L -10 -125 L -1 -138 L 8 -125 L 16 -134 L 16 -117 Z" fill={GOLD} stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M -17 -121 L 16.2 -121" stroke="#C99A1E" strokeWidth="2" />
          <circle cx="-1" cy="-128" r="2.6" fill="#D7263D" stroke={INK} strokeWidth="0.9" />
          <circle cx="-10" cy="-121.5" r="1.7" fill="#3E9BE6" stroke={INK} strokeWidth="0.8" />
          <circle cx="8" cy="-121.5" r="1.7" fill="#2F9E68" stroke={INK} strokeWidth="0.8" />
          {[-18, -1, 16].map((x, i) => <circle key={x} cx={x} cy={[-134, -138, -134][i]} r="1.6" fill="#FFF4C2" stroke={INK} strokeWidth="0.8" />)}
        </g>
      )
    case 'pirate':
      return (
        <g>
          <path d="M -32 -112 Q -24 -128 0 -134 Q 24 -128 32 -112 Q 18 -120 0 -118 Q -18 -120 -32 -112 Z" fill="#23262F" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M -26 -115.5 Q 0 -124 26 -115.5" fill="none" stroke={GOLD} strokeWidth="1.6" />
          <circle cx="0" cy="-125" r="3.4" fill="#F4F4F4" stroke={INK} strokeWidth="0.9" />
          <circle cx="-1.2" cy="-125.4" r="0.7" fill={INK} />
          <circle cx="1.2" cy="-125.4" r="0.7" fill={INK} />
          <path d="M -5 -119.5 L 5 -122.5 M -5 -122.5 L 5 -119.5" stroke="#F4F4F4" strokeWidth="1.3" strokeLinecap="round" />
        </g>
      )
    default:
      return null
  }
}

function Star({ x, y, r }: { x: number; y: number; r: number }) {
  const points = Array.from({ length: 10 }, (_, i) => {
    const angle = (Math.PI / 5) * i - Math.PI / 2
    const radius = i % 2 === 0 ? r : r * 0.45
    return `${(x + radius * Math.cos(angle)).toFixed(2)},${(y + radius * Math.sin(angle)).toFixed(2)}`
  }).join(' ')
  return <polygon points={points} fill={GOLD} stroke={INK} strokeWidth="0.6" strokeLinejoin="round" />
}

/**
 * An accessory on the face — the kids' ones (docs/refactor/48), free for everyone: a pink bubblegum bubble at the
 * mouth, or little stars painted on the cheeks.
 */
export function Extra({ kind }: { kind: ExtraKind }) {
  if (kind === 'bubblegum') {
    return (
      <g className="bh-bubblegum">
        <circle cx="5" cy="-83" r="7.5" fill="#FF8FC7" stroke={INK} strokeWidth="1.3" />
        <ellipse cx="2.4" cy="-86" rx="2.4" ry="1.6" fill="rgba(255,255,255,0.75)" />
      </g>
    )
  }
  if (kind === 'stars') {
    return (
      <g>
        <Star x={-14.5} y={-89} r={3.2} />
        <Star x={14.5} y={-89} r={3.2} />
      </g>
    )
  }
  return null
}

/** What is held in the hunting pose, in the racket's place and frame (Figures.tsx `Racket`). */
export function HeldItem({ kind }: { kind: Exclude<HeldKind, 'racket'> }) {
  let item: ReactNode = null
  if (kind === 'sword') {
    item = (
      <g>
        <path d="M -3.2 9 L -3.2 -27 L 0 -36 L 3.2 -27 L 3.2 9 Z" fill="#E4E9EF" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M 0 7 L 0 -30" stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
        <path d="M -1.6 -25 L -1.6 -10" stroke="rgba(255,255,255,0.85)" strokeWidth="0.9" strokeLinecap="round" />
        <rect x="-10" y="8.5" width="20" height="4.2" rx="2" fill={GOLD} stroke={INK} strokeWidth="1.3" />
        <rect x="-2.4" y="12.5" width="4.8" height="14" rx="1.6" fill="#5B3A1E" stroke={INK} strokeWidth="1.2" />
        <circle cx="0" cy="28.5" r="3" fill={GOLD} stroke={INK} strokeWidth="1.2" />
      </g>
    )
  } else if (kind === 'staff') {
    item = (
      <g>
        <path d="M 0 -26 L 0 32" stroke={INK} strokeWidth="5.2" strokeLinecap="round" />
        <path d="M 0 -26 L 0 32" stroke={WOOD} strokeWidth="3.2" strokeLinecap="round" />
        <path d="M -5 -27 Q -7 -36 0 -40 Q 7 -36 5 -27" fill="none" stroke={INK} strokeWidth="2.6" />
        <path d="M -5 -27 Q -7 -36 0 -40 Q 7 -36 5 -27" fill="none" stroke={WOOD} strokeWidth="1.2" />
        <circle cx="0" cy="-33" r="7.5" fill="rgba(183,156,255,0.35)" className="bh-glow" />
        <circle cx="0" cy="-33" r="4.6" fill="#B79CFF" stroke={INK} strokeWidth="1.2" />
        <circle cx="-1.4" cy="-34.6" r="1.4" fill="rgba(255,255,255,0.8)" />
      </g>
    )
  } else if (kind === 'axe') {
    item = (
      <g>
        <path d="M 0 -24 L 0 30" stroke={INK} strokeWidth="5" strokeLinecap="round" />
        <path d="M 0 -24 L 0 30" stroke={WOOD} strokeWidth="3" strokeLinecap="round" />
        <path d="M 1.5 -23 Q 17 -31 21 -13 Q 15 -9 1.5 -11 Z" fill={STEEL} stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M 19.5 -24 Q 23 -17 19.5 -11" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M -1.5 -20 L -8 -17 L -1.5 -14 Z" fill={STEEL_DARK} stroke={INK} strokeWidth="1.1" strokeLinejoin="round" />
      </g>
    )
  }
  return <g transform="translate(41 -117) rotate(18)">{item}</g>
}

/**
 * The RPG and sport outfits, over the same torso as the rest: armour (steel, the person's colour in the emblem), a
 * wizard's robe (their colour, trimmed in gold), a team shirt (their colour, a number and stripes).
 */
export function ProOutfit({ outfit, shirt, torso }: { outfit: 'armor' | 'robe' | 'jersey'; shirt: string; torso: string }) {
  if (outfit === 'armor') {
    return (
      <g>
        <path d={torso} fill={STEEL} stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M -19 -60 Q 0 -55 19 -60 M -20 -48 Q 0 -43 20 -48" fill="none" stroke={STEEL_DARK} strokeWidth="1.4" />
        <path d="M 0 -75 L 0 -38" stroke={STEEL_DARK} strokeWidth="1.2" />
        <ellipse cx="-16" cy="-72" rx="7.5" ry="5.5" fill={STEEL} stroke={INK} strokeWidth="1.4" />
        <ellipse cx="16" cy="-72" rx="7.5" ry="5.5" fill={STEEL} stroke={INK} strokeWidth="1.4" />
        <path d="M -6 -68 L 6 -68 L 6 -61 Q 0 -55 -6 -61 Z" fill={shirt} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M -12 -66 Q -9 -69 -6 -67" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="1.3" strokeLinecap="round" />
      </g>
    )
  }
  if (outfit === 'robe') {
    return (
      <g>
        <path d="M -11 -75.5 L 0 -58 L 11 -75.5" fill="none" stroke={GOLD} strokeWidth="2.4" strokeLinejoin="round" />
        <path d="M 0 -58 L 0 -36.5" stroke={GOLD} strokeWidth="2" />
        <path d="M -20 -47 Q 0 -43 20 -47" fill="none" stroke="#7A4E1E" strokeWidth="2.6" />
        <path d="M 4 -45 L 6 -38 M 7 -45 L 10 -39" stroke="#7A4E1E" strokeWidth="1.6" strokeLinecap="round" />
        <Star x={-10} y={-62} r={2.6} />
      </g>
    )
  }
  return (
    <g>
      <path d="M -8 -75.5 L 0 -68 L 8 -75.5" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M -17.5 -70 L -19.6 -42 M 17.5 -70 L 19.6 -42" stroke="rgba(255,255,255,0.85)" strokeWidth="2.4" />
      <text x="0" y="-48" textAnchor="middle" fontSize="15" fontWeight="800" fontFamily="system-ui, sans-serif" fill="#FFFFFF" stroke={INK} strokeWidth="0.8" paintOrder="stroke">
        10
      </text>
    </g>
  )
}

/** A small companion floating beside the head, away from the arm that holds things; it bobs on its own. */
export function Companion({ kind }: { kind: CompanionKind }) {
  if (kind === 'none') return null
  return (
    <g transform="translate(-44 -112)">
      <g className="bh-companion">{kind === 'dragon' ? <Dragon /> : <Owl />}</g>
    </g>
  )
}

function Dragon() {
  const scale = '#3FAE6B'
  const belly = '#F2D9B8'
  return (
    <g>
      <path d="M 6 6 Q 16 8 15 -1 Q 19 3 18 8 Q 14 14 4 10 Z" fill={scale} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M -3 -4 Q -14 -16 -16 -4 Q -11 -8 -8 -2 Z" fill="#2E8B57" stroke={INK} strokeWidth="1.1" strokeLinejoin="round" className="bh-companion__wing" />
      <ellipse cx="0" cy="3" rx="8" ry="7" fill={scale} stroke={INK} strokeWidth="1.3" />
      <ellipse cx="1" cy="5" rx="4.5" ry="4" fill={belly} />
      <circle cx="3" cy="-7" r="6.5" fill={scale} stroke={INK} strokeWidth="1.3" />
      <path d="M -1 -12 L -2 -17 L 1.5 -13 M 5 -12.5 L 6 -17.5 L 7.5 -12" fill={belly} stroke={INK} strokeWidth="1" strokeLinejoin="round" />
      <ellipse cx="8" cy="-5.5" rx="3" ry="2.2" fill={scale} stroke={INK} strokeWidth="1" />
      <circle cx="4.5" cy="-8" r="1.5" fill={INK} />
      <circle cx="4.9" cy="-8.4" r="0.5" fill="#FFFFFF" />
      <circle cx="9.5" cy="-6" r="0.5" fill={INK} />
    </g>
  )
}

function Owl() {
  const feather = '#A4774E'
  return (
    <g>
      <path d="M -7 -9 L -6 -15 L -2 -10 Z M 7 -9 L 6 -15 L 2 -10 Z" fill={feather} stroke={INK} strokeWidth="1.1" strokeLinejoin="round" />
      <ellipse cx="0" cy="0" rx="8.5" ry="10" fill={feather} stroke={INK} strokeWidth="1.3" />
      <ellipse cx="0" cy="3.5" rx="5" ry="5.5" fill="#E8D3B5" />
      <circle cx="-3.4" cy="-4" r="3.2" fill="#FFFFFF" stroke={INK} strokeWidth="0.9" />
      <circle cx="3.4" cy="-4" r="3.2" fill="#FFFFFF" stroke={INK} strokeWidth="0.9" />
      <circle cx="-3" cy="-3.8" r="1.4" fill={INK} />
      <circle cx="3.8" cy="-3.8" r="1.4" fill={INK} />
      <path d="M -1.2 -1.5 L 1.2 -1.5 L 0 0.8 Z" fill="#F2A541" stroke={INK} strokeWidth="0.7" strokeLinejoin="round" />
      <path d="M -8 1 Q -12 4 -8 7 M 8 1 Q 12 4 8 7" fill="none" stroke={INK} strokeWidth="1" />
    </g>
  )
}
